import React, { useContext, useState } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { AuthContext, IntlContext } from "context";
import { Loader2 } from "lucide-react";
import dataFetcher from "hooks/bookService";
import CalculOpperator from "helpers/CalculOpperator";
import roundNumber from "helpers/roundNumber";
import { toast } from "sonner";

type Props = {
    bookingId: string;
    providerId: string;
    clientId: string;
    hourlyRate: number;
    nbHours: number;
    cesuPlusActive: boolean;
    onSuccess: () => void;
};

const OfferPaymentForm: React.FC<Props> = ({
    bookingId,
    providerId,
    clientId,
    hourlyRate,
    nbHours,
    cesuPlusActive,
    onSuccess,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const [error, setError] = useState<string | undefined>();
    const [isPaying, setIsPaying] = useState(false);

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsPaying(true);
        setError(undefined);

        try {
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: "if_required",
            });

            if (stripeError) {
                if (stripeError.type === "card_error" || stripeError.type === "validation_error") {
                    setError(stripeError.message);
                }
                setIsPaying(false);
                return;
            }

            if (paymentIntent && paymentIntent.status === "requires_capture") {
                const totalPrice = hourlyRate * nbHours;
                const opperator = new CalculOpperator(null);
                const [managementFees, totalWithFees] = opperator.computeTTC(totalPrice);
                let totalAmount = cesuPlusActive ? managementFees : totalWithFees;

                if (account?.resources?.client?.[0]?.company_percentage) {
                    const supportedCompany =
                        totalAmount *
                        (parseFloat(account.resources.client[0].company_percentage) / 100);
                    totalAmount -= supportedCompany;
                }

                await dataFetcher.createConfirmPayment({
                    booking_id: bookingId,
                    provider_id: providerId,
                    client_id: clientId,
                    payment_intent: paymentIntent.id,
                    amount: roundNumber(totalAmount) * 100,
                });

                toast.success(intl.formatMessage("booking.messages.confirmation.payment.success"));
                onSuccess();
            }
        } catch {
            toast.error(intl.formatMessage("generic.error"));
            setIsPaying(false);
        }
    };

    return (
        <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4">
            <PaymentElement />
            {error ? <p className="text-sm text-[#ef4444]">{error}</p> : null}
            <button
                type="submit"
                disabled={!stripe || isPaying}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#00bc7d] py-3 text-[14px] font-black text-white transition-colors hover:bg-[#00a36d] disabled:opacity-50"
            >
                {isPaying ? (
                    <>
                        <Loader2 className="size-4 animate-spin" />
                        {intl.formatMessage("our_services.form.action.pay")}
                    </>
                ) : (
                    intl.formatMessage("booking.messages.confirmation.action_pay")
                )}
            </button>
        </form>
    );
};

export default OfferPaymentForm;
